import {
  Alert,
  Box,
  Button,
  Flex,
  Grid,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  TitleOrder,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { PiFloppyDisk, PiLink, PiPackage, PiTrash } from 'react-icons/pi';
import { Link } from 'wouter';
import CopyInput from '@/components/common/CopyInput';
import DateSinceDisplay from '@/components/common/DateSinceDisplay';
import Drawer from '@/components/drawer/Drawer';
import { PriceInput } from '@/components/PriceInput';
import ServiceDeletionConfirmation from '@/components/ServiceDeletionConfirmation';
import { formatPrice } from '@/lib/helpers';
import {
  TChildService,
  TService,
  useChildServices,
  useService,
  useServices,
} from '@/models/services';
import AgentServices from './AgentServices';
import GeneralDetails, { RenewalSection } from './GeneralDetails';
import IncludedServices from './IncludedServices';

export default function ServiceEditor({
  title,
  titleOrder = 2,
  service,
  allowDelete = true,
  initialCurrency = 'EUR',
  hideStandaloneFlag = false,
  initialJurisidctions,
  onSave,
  onDelete,
}: {
  title: string;
  titleOrder?: TitleOrder;
  service?: TService;
  allowDelete?: boolean;
  initialCurrency?: string;
  initialJurisidctions?: string[];
  hideStandaloneFlag?: boolean;
  onSave: (service: Partial<TService> | Omit<TService, 'id'>) => void;
  onDelete?: (service: TService) => void;
}) {
  const [fixedPrice, setFixedPrice] = useState(0);
  const [selectedChildService, setSelectedChildService] =
    useState<TChildService | null>(null);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false);
  const [
    confirmationOpened,
    { open: openConfirmation, close: closeConfirmation },
  ] = useDisclosure(false);

  const { data: children } = useChildServices(service?.id || 0);
  // Get complete service data with parents field populated
  const { data: fullService } = useService(service?.id || 0);

  const handleServiceClick = (childService: TChildService) => {
    setSelectedChildService(childService);
    openDrawer();
  };

  // Get the full service data for the selected child service to access parents
  const { data: selectedFullService } = useService(
    selectedChildService?.id || 0,
  );

  const handleDeleteClick = () => {
    if (service) {
      openConfirmation();
    }
  };

  const handleDeleteConfirm = () => {
    if (service) {
      onDelete?.(service);
    }
  };

  const form = useForm<
    Omit<TService, 'id' | 'children' | 'parents' | 'updatedAt'>
  >({
    mode: 'uncontrolled',
    validate: {
      name: (value) =>
        value.length < 3 ? 'Name must be at least 3 characters' : null,
      price: (value) => (value < 0 ? 'Price must be greater than 0' : null),
    },
  });

  useEffect(() => {
    form.setInitialValues({
      name: '',
      description: '',
      price: 0,
      jurisdictions: initialJurisidctions || [],
      standalone: false,
      isAdditionalService: false,
      isMandatory: false,
      invoiceTermsProfile: 'standard',
      type: 'Corporate Services',
      industry: 'Simple Company',
      currency: initialCurrency,
      renewal: {
        period: 0,
      },
      ...service,
    });
    form.reset();
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, initialCurrency, initialJurisidctions]);

  return (
    <form
      onSubmit={form.onSubmit((updatedService, event) => {
        event?.preventDefault();
        event?.stopPropagation();
        onSave(updatedService);
      })}
    >
      <Flex justify="space-between" align="center">
        <Stack gap={0} my={'xl'}>
          <Title order={titleOrder} fw={300}>
            {children && children.length > 0 ? <PiPackage /> : null}
            {title}
          </Title>
          {service && (
            <Group>
              <Group gap={5}>
                <Text size="xs" c="dimmed">
                  Last edited:
                </Text>
                <DateSinceDisplay
                  date={service.updatedAt}
                  size="xs"
                  c="dimmed"
                />
              </Group>
              <CopyInput value={`ID: ${service.id}`} />
            </Group>
          )}
        </Stack>
        <Group>
          {service?.id && allowDelete && (
            <Button
              size="xs"
              variant="light"
              color="red"
              leftSection={<PiTrash />}
              onClick={handleDeleteClick}
            >
              Delete
            </Button>
          )}
          <Button
            size="xs"
            variant="light"
            leftSection={<PiFloppyDisk />}
            disabled={!form.isDirty()}
            type="submit"
          >
            Save
          </Button>
        </Group>
      </Flex>
      <Grid>
        <Grid.Col span={service ? 5 : 12}>
          <Paper withBorder>
            <GeneralDetails
              hideStandaloneFlag={
                hideStandaloneFlag || (service?.parents?.length || 0) > 0
              }
              form={form}
            />
          </Paper>
          {service && (
            <Paper withBorder mt="md">
              <RenewalSection form={form} />
            </Paper>
          )}
        </Grid.Col>
        {service && (
          <Grid.Col span={7}>
            <>
              {!service.parents?.length ? (
                <Paper withBorder mb={'md'}>
                  <IncludedServices
                    parentService={service}
                    onChanged={setFixedPrice}
                    onServiceClick={handleServiceClick}
                  />
                </Paper>
              ) : (
                <Alert mb={'md'} color="yellow">
                  <ParentServices ids={service.parents} />
                </Alert>
              )}
              <Paper withBorder p={'md'} mb={'md'}>
                {fixedPrice === 0 ? (
                  <PriceInput
                    label="Price"
                    required
                    key={form.key('price')}
                    {...form.getInputProps('price')}
                    currencyLocked={
                      (service?.parents?.length || 0) > 0 ||
                      (children?.length || 0) > 0
                    }
                    currencyProps={{
                      key: form.key('currency'),
                      ...form.getInputProps('currency'),
                    }}
                  />
                ) : (
                  <>
                    <Text size="sm">
                      Sum {formatPrice(fixedPrice, service.currency)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Since this product includes other services, we always
                      calculate sum of prices of included services.
                    </Text>
                  </>
                )}
              </Paper>
              <Paper withBorder>
                <Text m="sm" fz={'sm'} fw={500}>
                  Agent provided services
                </Text>
                <Box p="sm">
                  <AgentServices service={service} />
                </Box>
              </Paper>
            </>
          </Grid.Col>
        )}
      </Grid>

      <Drawer opened={drawerOpened} onClose={closeDrawer} size={900}>
        <Box p={'md'}>
          {selectedChildService && selectedFullService && (
            <ServiceEditor
              title={selectedChildService.name}
              titleOrder={3}
              service={selectedFullService}
              allowDelete={false}
              onSave={(updatedService) => {
                onSave(updatedService);
                closeDrawer();
              }}
              onDelete={(serviceToDelete) => {
                onDelete?.(serviceToDelete);
                closeDrawer();
              }}
            />
          )}
        </Box>
      </Drawer>

      {/* Service Deletion Confirmation */}
      {service && allowDelete && (
        <ServiceDeletionConfirmation
          opened={confirmationOpened}
          onClose={closeConfirmation}
          onConfirm={handleDeleteConfirm}
          serviceName={service.name}
          parentIds={fullService?.parents || []}
        />
      )}
    </form>
  );
}

function ParentServices({ ids }: { ids: number[] }) {
  const { data: parents = [], isLoading } = useServices(ids);

  if (isLoading) {
    return null;
  }

  return (
    <Flex direction="column" gap={'xs'} m={'sm'}>
      <Text size={'xs'}>This service is included in:</Text>
      {parents.map((service) => (
        <Link key={service!.id} to={`/${service!.id}`}>
          <Group gap={5}>
            <PiLink size={16} />
            <Text p={0} size={'xs'}>
              {service!.name}
            </Text>
          </Group>
        </Link>
      ))}
    </Flex>
  );
}
